class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    // 2026-10-07 a11y: callers that open the drawer without naming the opener (cart:refresh) would leave nothing
    // to return focus to; remember whatever was focused outside the drawer at that moment.
    if (!focusElement && !this.open) {
      const current = document.activeElement;
      if (current && current !== document.body && !this.contains(current)) focusElement = current;
    }
    super.show(focusElement, animate);

    if (this.open) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  hide() {
    // 2026-10-07 a11y: return focus to the opener as soon as the drawer starts closing. The base class makes the
    // drawer inert at once, which drops focus to <body> for the 0.8s overlay transition until handleAfterHide
    // restores it; a keyboard user who pressed Escape would otherwise start the next Tab from the top of the page.
    const wasOpen = this.open;
    const opener = this.activeElement;
    const result = super.hide();
    if (wasOpen && opener && opener.isConnected) {
      opener.focus();
      // Clear it so handleAfterHide (theme.js) does not focus the opener a second time after the transition,
      // which would undo a Tab or click the customer made in between. removeTrapFocus(null) still unbinds the trap.
      this.activeElement = null;
    }
    return result;
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
      cartElement.innerHTML = newCartContent;

      if (event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

// ZestiGreens cart sync (push 50), built on the purchased theme's own rendering. Three additions only:
//  1. quantity/removal changes and cart confirmations run one at a time, in order, so replies can never arrive out of
//     order or overlap a confirmation;
//  2. every change names its line item key (`id`, which Shopify applies over `line`), so it always hits the intended
//     product, even after a removal;
//  3. every failure ends in one confirmation: the server's cart is shown, or every cart shows a visible
//     "couldn't confirm" alert.
// Other features (product add, bundles, FoxKit, gift wrap) publish full carts on their own. A reply that may be older
// than such a cart is shown only if it agrees with what is displayed; otherwise the cart is confirmed (once more).
const zestiCart = {
  queue: Promise.resolve(),
  external: 0, // full-cart publications made by other features
  lastEvent: null, // last publication handled (each is rendered by both hosts but counted/handled once)
  shown: null, // displayed cart: item key -> quantity
  held: null, // newest server cart waiting for an unsent edit to be sent or to lapse
  seq: 0, // order of queued changes
  latest: {}, // item identity -> seq of its newest queued change (push 51)
};
const zestiCartError = 'There was an error while updating your cart. Please try again.';
const zestiCartUnconfirmed = "We couldn't confirm your cart. Please refresh the page to see your latest items.";
const zestiKeyOf = (item) => String(item.key || item.id || item.variant_id);
const zestiQtyMap = (cart) => Object.fromEntries((cart.items || []).map((i) => [zestiKeyOf(i), i.quantity]));
// Does this full cart match the displayed one, every item included? Absent items count as 0.
const zestiAgrees = (cart) => {
  if (!zestiCart.shown) return true;
  const next = zestiQtyMap(cart);
  const keys = new Set([...Object.keys(next), ...Object.keys(zestiCart.shown)]);
  return [...keys].every((k) => (next[k] || 0) === (zestiCart.shown[k] || 0));
};
// Push 53: quantities compare as numbers ("3.0" and "3" are the same quantity); a non-numeric value keeps its text.
const zestiNum = (v) => {
  const n = Number(v);
  return v !== '' && v != null && Number.isFinite(n) ? String(n) : String(v);
};
// A quantity box holding a value the shopper typed that has not been sent yet (an invalid value is reset by the theme).
const zestiUnsentEdit = () =>
  [...document.querySelectorAll('cart-items [name="updates[]"]')].some(
    (i) => zestiNum(i.value) !== zestiNum(i.defaultValue) && i.dataset.zestiSent !== zestiNum(i.value)
  );
const zestiEnqueue = (task) => (zestiCart.queue = zestiCart.queue.then(task).catch(() => {}));

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content', 'w-full');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    // Push 54: one conversion everywhere — Number(), as the browser and zestiNum read it ("3e1" is 30, "3.0" is 3);
    // parseInt would send 3 for "3e1". An unreadable value gets the theme's minimum message and reset.
    const inputValue = Number(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (!Number.isFinite(inputValue) || target.value === '') {
      message = FoxTheme.quickOrderListStrings.min_error.replace('[min]', target.getAttribute('data-min') || '1');
    } else if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = FoxTheme.quickOrderListStrings.min_error.replace('[min]', target.getAttribute('data-min'));
    } else if (inputValue > parseInt(target.max)) {
      message = FoxTheme.quickOrderListStrings.max_error.replace('[max]', target.max);
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = FoxTheme.quickOrderListStrings.step_error.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    } else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      // Both cart hosts receive the same publication; alert and roll back once (the theme did both twice).
      if (zestiCart.lastEvent === event) return;
      zestiCart.lastEvent = event;
      this.onCartError(event.cart.errors, event.target);
      return;
    }
    // Every full cart rendered (by this file or another feature) becomes the displayed cart; count each publication once.
    if (Array.isArray(event.cart.items) && typeof event.cart.item_count === 'number' && zestiCart.lastEvent !== event) {
      zestiCart.lastEvent = event;
      if (event.sources !== 'cart-items') zestiCart.external += 1;
      zestiCart.shown = zestiQtyMap(event.cart);
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const typed = this.zestiTypedValues(sectionId); // push 51: unsent values survive any full-cart render
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      // const updatedElement = sectionToRender.querySelector(`#CartDrawer-${sectionId}`);
      // if (updatedElement) {
      //   cartDrawer.innerHTML = updatedElement.innerHTML;
      // }
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    this.zestiRestoreTyped(sectionId, typed);

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  // Push 51: values the shopper typed but whose request has not been sent yet, by item (line item key, else variant).
  zestiTypedValues(sectionId) {
    const host = document.querySelector(`#shopify-section-${sectionId}`);
    if (!host) return [];
    return [...host.querySelectorAll('cart-items [name="updates[]"]')]
      .filter((i) => zestiNum(i.value) !== zestiNum(i.defaultValue) && i.dataset.zestiSent !== zestiNum(i.value))
      .map((i) => ({ item: this.zestiItemOf(i, sectionId), value: String(i.value) }))
      .filter((t) => t.item);
  }

  // After a render, put each unsent value back into its item's new input and queue it at once through the theme's own
  // validation (a render had replaced the input before its request could be queued). Push 52: each value is queued
  // directly; refiring `change` would pass all of them through the list's single 300 ms debounce, which keeps only one.
  zestiRestoreTyped(sectionId, typed) {
    if (!typed.length) return;
    const host = document.querySelector(`#shopify-section-${sectionId}`);
    if (!host) return;
    host.querySelectorAll('cart-items [name="updates[]"]').forEach((i) => {
      const t = typed.find((x) => x.item === this.zestiItemOf(i, sectionId));
      if (!t || zestiNum(i.defaultValue) === zestiNum(t.value)) return;
      i.value = t.value;
      const owner = i.closest('cart-items');
      if (owner && typeof owner.validateQuantity === 'function') owner.validateQuantity({ target: i });
    });
  }

  zestiItemOf(input, sectionId) {
    const key = this.zestiLineKey(input.dataset.index, sectionId);
    if (key) return 'k:' + key;
    const variant = input.dataset.quantityVariantId;
    return variant ? 'v:' + variant : null;
  }

  onCartError(errors, target) {
    if (target) {
      // Push 52: the rollback restores the previous quantity, so the field shows it too (the rejected value is not an
      // unsent edit). A newer typed value never reaches here: zestiSend skips the rollback for it.
      target.value = target.defaultValue;
      this.updateQuantity(
        target.getAttribute('data-index'),
        target.defaultValue,
        document.activeElement.getAttribute('name'),
        target,
        true
      );
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }

    alert(errors);
  }

  updateQuantity(line, quantity, name, target, isRollback = false) {
    if (!this.isConnected) return; // a re-render replaced this list; its pending debounce must not act on old markup
    const sectionId = FoxTheme.utils.getSectionId(this);
    this.showLoader(line);
    // Push 52: mark the quantity actually sent (a rollback sends the old value, not what the field may now hold).
    if (target) target.dataset.zestiSent = zestiNum(quantity);
    zestiCart.held = null; // this change's own reply will show a newer cart (or its failure confirms one)

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const key = this.zestiLineKey(line, sectionId);
    const ident = key || 'line:' + line;
    zestiCart.seq += 1;
    zestiCart.latest[ident] = zestiCart.seq;
    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
      ...(key ? { id: key } : {}),
    });
    const job = { line, quantity, name, target, isRollback, sectionId, sections: sectionsToBundle, key, ident, seq: zestiCart.seq, body };
    zestiEnqueue(() => this.zestiSend(job));
  }

  zestiSend(job) {
    const external0 = zestiCart.external;
    return fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body: job.body } })
      .then((response) => response.json().then((state) => ({ ok: response.ok, state })))
      .then(({ ok, state }) => {
        // The theme's own contract for 200 `errors` payloads (alert + one rollback); a rollback that fails confirms.
        if (ok && state && state.errors) {
          if (job.isRollback) return this.zestiFail(job, '');
          // Push 51: a newer change to this item is already queued and owns its result, so the theme's automatic
          // rollback (which would send the old quantity after it) is skipped; the message is still shown.
          // ...or the shopper has typed a newer value into this field that is about to be sent.
          const typedSince = job.target && job.target.isConnected && zestiNum(job.target.value) !== zestiNum(job.quantity);
          if ((zestiCart.latest[job.ident] || 0) > job.seq || typedSince) {
            document.querySelectorAll(`#Loader-${job.sectionId}-${job.line}`).forEach((l) => l.classList.remove('btn--loading'));
            const live = this.zestiLive(job.sectionId);
            if (live) live.showUpdateError(typeof state.errors === 'string' ? state.errors : zestiCartError);
            return;
          }
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            sources: 'cart-items',
            cart: state,
            target: job.target,
            line: job.line,
            name: job.name,
          });
          return;
        }
        if (!ok || !state || state.status || !this.zestiSectionsValid(state.sections, job.sections)) {
          // Shopify's own wording is shown for cart limits (422); other failures get the plain message.
          const message = state && Number(state.status) === 422 ? state.description || state.message : '';
          return this.zestiFail(job, message);
        }
        const live = this.zestiLive(job.sectionId);
        if (live) live.clearUpdateError();
        this.zestiShow({
          payload: { sources: 'cart-items', cart: state, target: job.target, line: job.line, name: job.name },
          external0,
          sectionId: job.sectionId,
          sections: job.sections,
          message: null,
          retried: false,
        });
      })
      .catch(() => this.zestiFail(job, ''));
  }

  // Show a server cart. Held while an unsent edit would be wiped (that edit's own reply shows a newer cart, or the edit
  // lapses). If another feature published a cart since this one was requested and the two disagree, their order is
  // unknown: confirm the cart instead (once; a second disagreement ends in the visible alert).
  zestiShow(entry) {
    zestiCart.held = entry;
    const release = () => {
      if (zestiCart.held !== entry) return;
      if (zestiUnsentEdit()) {
        setTimeout(release, 300);
        return;
      }
      zestiCart.held = null;
      // Push 51: compared in full, the changed item included (another feature may have changed that same item).
      if (zestiCart.external !== entry.external0 && !zestiAgrees(entry.payload.cart)) {
        if (entry.retried) return this.zestiUnconfirmed();
        return this.zestiConfirm(entry.sectionId, entry.sections, entry.message, true);
      }
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, entry.payload);
      if (entry.message) {
        const live = this.zestiLive(entry.sectionId);
        if (live) live.showUpdateError(entry.message);
      }
    };
    release();
  }

  // Failed change: say so, restore the input (unless the shopper has typed a newer value) and confirm the cart.
  zestiFail(job, message) {
    const text = message || zestiCartError;
    document.querySelectorAll(`#Loader-${job.sectionId}-${job.line}`).forEach((l) => l.classList.remove('btn--loading'));
    const t = job.target;
    const editedSince = t && t.isConnected && zestiNum(t.value) !== zestiNum(job.quantity);
    if (!editedSince && t && t.isConnected && typeof t.defaultValue !== 'undefined') t.value = t.defaultValue;
    const live = this.zestiLive(job.sectionId);
    if (live) live.showUpdateError(text);
    this.zestiConfirm(job.sectionId, job.sections, text, false);
  }

  // One confirmation read of the whole cart, queued behind any change still to be sent. It ends with the server's cart
  // shown (keeping `message`), or with the visible "couldn't confirm" alert in every cart; no automatic retry loop.
  zestiConfirm(sectionId, sections, message, retried) {
    const ids = (sections || []).filter(Boolean);
    zestiEnqueue(() => {
      if (!ids.length) return this.zestiUnconfirmed();
      const external0 = zestiCart.external;
      return Promise.all([
        fetch(`${FoxTheme.routes.cart_url}.js`).then((r) => (r.ok ? r.json() : null)),
        fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`).then((r) => (r.ok ? r.json() : null)),
      ])
        .then(([cart, rendered]) => {
          if (!cart || cart.status || typeof cart.item_count !== 'number' || !this.zestiSectionsValid(rendered, ids)) {
            return this.zestiUnconfirmed();
          }
          cart.sections = rendered;
          this.zestiShow({ payload: { sources: 'cart-items', cart }, external0, sectionId, sections, message, retried });
        })
        .catch(() => this.zestiUnconfirmed());
    });
  }

  zestiUnconfirmed() {
    document.querySelectorAll('cart-items').forEach((el) => {
      if (el.isConnected && typeof el.showUpdateError === 'function') el.showUpdateError(zestiCartUnconfirmed);
    });
  }

  // Line item key from the row's remove link (/cart/change?id=KEY&quantity=0); null if it cannot be read.
  zestiLineKey(line, sectionId) {
    const link = document.querySelector(`#Loader-${sectionId}-${line}[href]`);
    try {
      const key = link ? new URL(link.getAttribute('href'), window.location.href).searchParams.get('id') : null;
      return key && /^\d+:[a-f0-9]{32}$/.test(key) ? key : null;
    } catch (e) {
      return null;
    }
  }

  zestiSectionsValid(sections, required) {
    if (!sections || typeof sections !== 'object') return false;
    return (required || []).every((id) => typeof sections[id] === 'string' && sections[id].trim() !== '');
  }

  zestiLive(sectionId) {
    return document.querySelector(`#shopify-section-${sectionId} cart-items`) || (this.isConnected ? this : null);
  }

  showUpdateError(message) {
    let el = this.querySelector('[data-zesti-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('role', 'alert');
      el.setAttribute('data-zesti-cart-error', '');
      el.className = 'zesti-cart-error';
      this.prepend(el);
    }
    el.textContent = message;
  }

  clearUpdateError() {
    const el = this.querySelector('[data-zesti-cart-error]');
    if (el) el.remove();
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }
}

customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });
class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class CalculateShipping extends ModalComponent {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    } else {
      const userAgent = navigator.userAgent;
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      if (isSafari) {
        this.init();
      } else {
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
      }
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  init() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations && recommendations.innerHTML.trim().length) {
          const productCount = recommendations.querySelectorAll('.product-card');
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove('hidden');
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section').remove();
          this.classList.add('hidden');
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
  }
}

customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};
